import React, { useContext, useEffect, useMemo, useState } from 'react';
import ArrowRight from '@untitled-ui/icons-react/build/cjs/ArrowRight';
import { UMButton } from '@urbanmetrix/um-kit';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from 'components/lib';
import { ProjectAddExitContext } from 'contexts/ProjectAddExitContext';
import { LastAddedItemContext } from 'contexts/LastAddedItemContext';
import { ProjectContext } from 'contexts/ProjectContext';
import { GridContext } from 'contexts/GridContext';
import { GeodataContext } from 'contexts/GeodataContext';
import { SimulationContext } from 'contexts/SimulationContext';
import { ValidationContext } from 'contexts/ValidationContext';
import { ValidationMinMaxContext } from 'contexts/ValidationMinMaxContext';
import { addProject } from 'api/projectApi';
import { wgs84ToLv95ForOneCoordinate } from 'views/add-grid/map/coordinateTransforms';
import ProjectCreationShell from 'views/project-creation/ProjectCreationShell';
import MandatoryFields from './MandatoryFields';
import { clearPendingCountsTowardUsage, getPendingCountsTowardUsage } from '../../services/countsTowardUsage';
import type { ProjectInsert } from '@urbanmetrix/sdk';

interface AddProjectProps {
  onProjectCreated?: (projectId: string) => void | Promise<void>;
  breadcrumbTitle?: React.ReactNode;
}

const SWITZERLAND = 'SWITZERLAND' as ProjectInsert['country'];

const AddProject: React.FC<AddProjectProps> = ({ onProjectCreated, breadcrumbTitle }) => {
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);
  const { exitIntentNavigateTo, clearProjectAddExit } = useContext(ProjectAddExitContext);
  const { setLastAddedItem } = useContext(LastAddedItemContext);
  const { project } = useContext(ProjectContext);
  const { clearGridFormData } = useContext(GridContext);
  const { clearGeodataFormData } = useContext(GeodataContext);
  const { clearSimulationFormData } = useContext(SimulationContext);
  const { isProjectMunicipalityValid, showProjectValidationErrors, setShowProjectValidationErrors } =
    useContext(ValidationContext);
  const { isValidProjectMinMaxInputs } = useContext(ValidationMinMaxContext);

  const [countsTowardUsage, setCountsTowardUsage] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!authContext.permission?.support) {
      setCountsTowardUsage(true);
      return;
    }

    setCountsTowardUsage(getPendingCountsTowardUsage());
    clearPendingCountsTowardUsage();
  }, [authContext.permission?.support]);

  useEffect(() => {
    if (exitIntentNavigateTo === null) {
      return;
    }

    clearProjectAddExit();
    navigate(exitIntentNavigateTo);
  }, [clearProjectAddExit, exitIntentNavigateTo, navigate]);

  useEffect(() => {
    if (!showProjectValidationErrors) {
      return;
    }

    const timer = window.setTimeout(() => {
      setShowProjectValidationErrors(false);
    }, 3000);

    return () => {
      window.clearTimeout(timer);
    };
  }, [setShowProjectValidationErrors, showProjectValidationErrors]);

  const isValidProjectMinMaxFields = useMemo(
    () => Object.values(isValidProjectMinMaxInputs).every((value) => value === true),
    [isValidProjectMinMaxInputs]
  );

  const canSaveProject = useMemo(
    () =>
      String(project.name ?? '').trim() !== '' &&
      String(project.coordinates?.lat ?? '').trim() !== '' &&
      String(project.coordinates?.lng ?? '').trim() !== '' &&
      isProjectMunicipalityValid &&
      isValidProjectMinMaxFields,
    [
      isProjectMunicipalityValid,
      isValidProjectMinMaxFields,
      project.coordinates?.lat,
      project.coordinates?.lng,
      project.name,
    ]
  );

  const resetDependentForms = () => {
    clearGridFormData();
    clearGeodataFormData();
    clearSimulationFormData();
  };

  const createProject = async () => {
    const latRaw = String(project.coordinates?.lat ?? '').trim();
    const lngRaw = String(project.coordinates?.lng ?? '').trim();
    const parsedLat = Number.parseFloat(latRaw.replace(',', '.'));
    const parsedLng = Number.parseFloat(lngRaw.replace(',', '.'));
    const projectCoordinates = wgs84ToLv95ForOneCoordinate(parsedLng, parsedLat);

    return addProject({
      name: project.name,
      address: project.address?.trim() || null,
      projectCoordinates,
      country: SWITZERLAND,
      countsTowardUsage,
      numberOfBuildings: project.numberOfBuildings,
      buildingUse: project.buildingUse,
      grossFloorSurface: project.grossFloorSurface,
      numberOfFloors: project.numberOfFloors,
    });
  };

  const handleProjectCreated = async (createdProjectId: string) => {
    if (onProjectCreated) {
      await onProjectCreated(createdProjectId);
      return;
    }

    navigate(`/projects/add?step=grid&projectId=${createdProjectId}`);
  };

  const handleCreateAndContinue = async () => {
    setShowProjectValidationErrors(true);
    if (!canSaveProject || isSubmitting) {
      return;
    }

    try {
      setIsSubmitting(true);
      const createdProject = await createProject();
      const createdProjectId = createdProject?.id == null ? null : String(createdProject.id);

      resetDependentForms();
      setLastAddedItem(createdProject);

      if (createdProjectId) {
        await handleProjectCreated(createdProjectId);
        return;
      }

      navigate('/dashboard');
    } catch (error) {
      console.error('Error creating project:', error);
      setIsSubmitting(false);
    }
  };

  const handleExit = () => {
    const nextNavigateTo = exitIntentNavigateTo || '/dashboard';
    clearProjectAddExit();
    navigate(nextNavigateTo);
  };

  return (
    <ProjectCreationShell
      title={breadcrumbTitle ?? 'Add Project'}
      onClose={handleExit}
      footerRight={
        <UMButton
          text={isSubmitting ? 'Adding grid...' : 'ADD GRID'}
          onClick={handleCreateAndContinue}
          icon={ArrowRight}
          customClassName="onboarding-finish-button onboarding-finish-button--primary"
          loading={isSubmitting}
          disabled={isSubmitting || !canSaveProject}
        />
      }
    >
      <MandatoryFields />
    </ProjectCreationShell>
  );
};

export default AddProject;
